import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { useSearchParams, useLocation, useNavigationType } from 'react-router-dom';
import { get } from '@/features/shared/lib/api';
import { arr, parsePayload, buildListParams } from '@/features/shared/lib/utils';
import Paginator from '@/features/shared/ui/Paginator';
import PublicationCard from '@/features/publication/components/PublicationCard';
import JournalCard from "@/features/journal/components/JournalCard";
import { FacetBlock } from "@/features/shared/components/Facet";
// import PublicationGraphs from '@/features/publication/components/PublicationGraphs';
import ResearcherSortSelect from '@/features/researcher/components/ResearcherSortSelect';
import ResearcherGraphs from '@/features/researcher/components/ResearcherGraphs';
import ResearcherCard from "@/features/researcher/components/ResearcherCard";
import { FacetPopup } from "@/features/shared/components/FacetPopup";
import PageBreadcrumb from '@/features/shared/components/PageBreadcrumb';
import ResearchersFilterSidebar from '@/features/researcher/components/ResearchersFilterSidebar';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';

export default function Researchers() {
  // Back-navigation + cache helpers setup early so we can seed initial state to avoid flicker
  const location = useLocation();
  const navType = useNavigationType();
  const hydratedRef = useRef(false);

  const cacheKey = useMemo(() => {
    try {
      const { pathname, search } = window.location;
      return `RESEARCHERS_CACHE::${pathname}${search}`;
    } catch (e) {
      return 'RESEARCHERS_CACHE';
    }
  }, [location]);

  const loadCache = () => {
    try {
      const raw = sessionStorage.getItem(cacheKey);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };
  const saveCache = (payload) => {
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(payload));
    } catch (e) { }
  };

  // Cache TTL and validator (avoid using empty or stale caches)
  const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes
  const isValidCache = (c) => {
    if (!c) return false;
    if (!Array.isArray(c.rows) || c.rows.length === 0) return false;
    if (typeof c.ts !== 'number') return true; // backward compatibility
    return (Date.now() - c.ts) <= CACHE_TTL_MS;
  };

  // Detect real browser back/forward navigation (not reload/initial)
  const isBackForwardNav = () => {
    try {
      const navs = performance.getEntriesByType && performance.getEntriesByType('navigation');
      if (navs && navs.length) {
        const type = navs[0]?.type;
        if (type === 'back_forward') return true;
        if (type === 'reload') return false;
        return false;
      }
    } catch (e) { }
    // Fallback: use history.state.idx only when referrer is same-origin
    try {
      const idx = window.history && window.history.state && window.history.state.idx;
      const sameOriginReferrer = document.referrer && document.referrer.startsWith(window.location.origin);
      if (typeof idx === 'number' && idx > 0 && sameOriginReferrer) return true;
    } catch (e) { }
    return false;
  };

  // Preload cache when coming back to avoid first-paint flicker
  const initialCacheRaw = (navType === 'POP' && isBackForwardNav()) ? loadCache() : null;
  const initialCache = isValidCache(initialCacheRaw) ? initialCacheRaw : null;

  // Canonical states (seeded from cache when applicable)
  const [rows, setRows] = useState(Array.isArray(initialCache?.rows) ? initialCache.rows : []);
  const [pageInfo, setPageInfo] = useState(initialCache?.pageInfo || { fromPage: 1, totalPage: 1, resultsPerPage: 24, totalResults: 0 });
  const [facet, setFacet] = useState(initialCache?.facet || {});
  const [meta, setMeta] = useState(initialCache?.meta || {});
  // 초기 로딩 상태: 캐시가 없으면 true, 있으면 false
  const [loading, setLoading] = useState(!initialCache);
  const [err, setErr] = useState(null);
  // 첫 로딩인지 추적 (첫 API 호출일 때만 로딩 컴포넌트 표시)
  const isFirstLoadRef = useRef(!initialCache);

  // filters: { [facetKey]: string[] } - applied filters
  const [filters, setFilters] = useState(initialCache?.filters || {});
  // pendingFilters: staged selections applied on Refine
  const [pendingFilters, setPendingFilters] = useState(initialCache?.pendingFilters || initialCache?.filters || {});
  // conjunctions: { [facetKey]: 'and' | null } - conjunction state for facets
  const [conjunctions, setConjunctions] = useState({});
  // query search term (API/URL param: sub_ks)
  const [query, setQuery] = useState(typeof initialCache?.query === 'string' ? initialCache.query : '');
  const [pendingQuery, setPendingQuery] = useState(typeof initialCache?.pendingQuery === 'string' ? initialCache.pendingQuery : (typeof initialCache?.query === 'string' ? initialCache.query : ''));
  // sort: { order_by: string, order: 'asc'|'desc' }
  const [sort, setSort] = useState(initialCache?.sort || { order_by: 'date', order: 'desc' });
  const [popup, setPopup] = useState({ open: false, key: '', title: '' });
  // graph toggle state
  const [graphExpanded, setGraphExpanded] = useState(true);

  // URL에 검색 또는 패싯 관련 파라미터가 있으면 그래프 접기
  const checkIfSearchOrFacetMode = () => {
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get('sub_ks')) return true;
      if (sp.get('hot')) return true;
      if (sp.get('hot_year')) return true;
      let hasFilter = false;
      let hasConjunction = false;
      sp.forEach((_, k) => {
        if (k.endsWith('[]')) hasFilter = true;
        if (k.endsWith('_conjunction')) hasConjunction = true;
      });
      if (hasFilter || hasConjunction) return true;
      if (Number(sp.get('current_page')) > 1) return true;
      return false;
    } catch (e) {
      return false;
    }
  };

  const buildExtraParams = () => {
    const extra = {};
    // query (API param: sub_ks)
    if (query && String(query).trim() !== '') extra.sub_ks = String(query).trim();
    // facet arrays as key[]=value
    Object.entries(filters || {}).forEach(([k, arrVals]) => {
      if (k === 'hot') {
        // hot 필터는 hot[] 대신 hot으로 첫 번째 값만 전달
        if (Array.isArray(arrVals) && arrVals.length) {
          extra.hot = arrVals[0];
        }
      } else if (Array.isArray(arrVals) && arrVals.length) {
        extra[`${k}[]`] = arrVals;
      }
    });
    // hot_year 파라미터 추가
    if (filters?.hot_year) {
      extra.hot_year = filters.hot_year;
    }
    // conjunction parameters
    Object.entries(conjunctions || {}).forEach(([k, conj]) => {
      if (conj === 'and') {
        extra[`${k}_conjunction`] = 'and';
      }
    });
    if (sort?.order_by) extra.order_by = sort.order_by;
    if (sort?.order) extra.order = sort.order;
    return extra;
  };

  // Pre-paint hydration: if returning via POP and have a valid cache, inject state and restore scroll immediately
  useLayoutEffect(() => {
    if (navType !== 'POP' || hydratedRef.current) return;
    const cached = loadCache();
    if (isValidCache(cached)) {
      hydratedRef.current = true;
      setRows(cached.rows || []);
      setPageInfo(cached.pageInfo || pageInfo);
      setFacet(cached.facet || {});
      if (cached.meta) setMeta(cached.meta);
      if (cached.sort) setSort(cached.sort);
      if (cached.filters) {
        setFilters(cached.filters);
        setPendingFilters(cached.pendingFilters || cached.filters);
      }
      if (cached.conjunctions) {
        setConjunctions(cached.conjunctions);
      }
      if (typeof cached.query === 'string') setQuery(cached.query);
      if (typeof cached.pendingQuery === 'string') setPendingQuery(cached.pendingQuery);
      try {
        window.scrollTo({ top: cached.scrollY || 0, left: 0, behavior: 'auto' });
      } catch (e) { }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navType]);

  // journal_id로 진입한 경우(저널 상세 More 등) 스크롤 최상단
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.has('journal_id')) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [location.search]);

  const cleanFilters = (f = {}) => {
    const out = {};
    Object.entries(f).forEach(([k, vals]) => {
      if (Array.isArray(vals) && vals.length) out[k] = vals;
    });
    return out;
  };

  const fetchList = async (page = 1, opt = {}) => {
    // 첫 로딩일 때만 loading 상태를 true로 설정
    if (isFirstLoadRef.current) {
      setLoading(true);
    }
    setErr(null);
    try {
      const extra = opt.extra ? opt.extra : buildExtraParams();
      const params = buildListParams({ page, perPage: pageInfo.resultsPerPage || 21, extra });
      const data = await get('/rm/v1/scholars', { params });
      const { status, records, pageInfo: pi, facet: fc, meta: mt } = parsePayload(data);
      if (!status) throw new Error('API status=false');
      const nextRows = records;
      const nextPageInfo = {
        fromPage: pi.current_page ?? pi.fromPage ?? page,
        totalPage: pi.totalPage ?? pi.total_page ?? 1,
        resultsPerPage: pi.resultsPerPage ?? pi.per_page ?? params.per_page ?? 21,
        totalResults: pi.totalResults ?? pi.total_results ?? 0,
      };
      const nextFacet = fc || {};
      const nextMeta = mt || {};
      setRows(nextRows);
      setPageInfo(nextPageInfo);
      setFacet(nextFacet);
      setMeta(nextMeta);
      // save cache after successful fetch
      saveCache({
        rows: nextRows,
        pageInfo: nextPageInfo,
        facet: nextFacet,
        meta: nextMeta,
        filters,
        pendingFilters,
        conjunctions,
        sort,
        query,
        pendingQuery,
        scrollY: window.scrollY || 0,
        ts: Date.now(),
      });
    } catch (e) {
      console.error('[scholars][fetchList]', e);
      setErr(e?.response?.data?.message || e.message);
    } finally {
      if (isFirstLoadRef.current) {
        setLoading(false);
        isFirstLoadRef.current = false; // 첫 로딩 완료 후 플래그 해제
      }
    }
  };

  // URL 동기화 유틸
  const syncUrl = ({ page, filters: f = filters, sort: s = sort, query: q = query, conjunctions: c = {} } = {}) => {
    const sp = new URLSearchParams(window.location.search);
    // page
    if (page) sp.set('current_page', String(page)); else sp.delete('current_page');
    // sort
    if (s?.order_by) sp.set('order_by', s.order_by); else sp.delete('order_by');
    if (s?.order) sp.set('order', s.order); else sp.delete('order');
    // query (URL param: sub_ks)
    if (q && String(q).trim() !== '') sp.set('sub_ks', String(q).trim()); else sp.delete('sub_ks');
    // clear existing facet [] keys and conjunction keys
    const toDelete = [];
    sp.forEach((_, k) => {
      if (k.endsWith('[]')) toDelete.push(k);
      if (k.endsWith('_conjunction')) toDelete.push(k);
      if (k === 'hot') toDelete.push(k); // hot도 삭제
      if (k === 'hot_year') toDelete.push(k); // hot_year도 삭제
    });
    toDelete.forEach(k => sp.delete(k));
    // add current filters as key[]=value
    Object.entries(f || {}).forEach(([k, vals]) => {
      if (k === 'hot') {
        // hot 필터는 hot[] 대신 hot으로 첫 번째 값만 전달
        if (Array.isArray(vals) && vals.length) {
          sp.set('hot', vals[0]);
        }
      } else if (k === 'hot_year') {
        // hot_year는 단일 값
        if (vals) {
          sp.set('hot_year', String(vals));
        }
      } else if (Array.isArray(vals)) {
        vals.forEach(v => sp.append(`${k}[]`, v));
      }
    });
    // add conjunction parameters
    Object.entries(c || {}).forEach(([k, conj]) => {
      if (conj === 'and') {
        sp.set(`${k}_conjunction`, 'and');
      }
    });
    const newUrl = `${window.location.pathname}?${sp.toString()}`;
    window.history.replaceState({}, '', newUrl);
  };

  useEffect(() => {
    // On true back/forward with valid cache, skip initial fetch (already hydrated)
    if (navType === 'POP' && isBackForwardNav()) {
      const cached = loadCache();
      if (isValidCache(cached)) {
        hydratedRef.current = true;
        if (!rows || rows.length === 0) {
          setRows(cached.rows || []);
          setPageInfo(cached.pageInfo || pageInfo);
          setFacet(cached.facet || {});
          if (cached.meta) setMeta(cached.meta);
          if (cached.sort) setSort(cached.sort);
          if (cached.filters) setFilters(cached.filters);
          if (cached.pendingFilters) setPendingFilters(cached.pendingFilters);
          if (cached.conjunctions) setConjunctions(cached.conjunctions);
          if (typeof cached.query === 'string') setQuery(cached.query);
          if (typeof cached.pendingQuery === 'string') setPendingQuery(cached.pendingQuery);
        }
        setTimeout(() => {
          try { window.scrollTo({ top: cached.scrollY || 0, left: 0, behavior: 'auto' }); } catch (e) { }
        }, 0);
        return; // skip initial fetch
      }
    }

    // Normal initial fetch path
    const sp = new URLSearchParams(window.location.search);
    const initPage = Number(sp.get('current_page')) || 1;
    const hasKeyword = !!(sp.get('ks') || sp.get('sub_ks'));
    const initSort = { order_by: sp.get('order_by') || (hasKeyword ? 'score' : 'date'),
      order: sp.get('order') || 'desc' };


    const initFilters = {};
    const initConjunctions = {};
    sp.forEach((v, k) => {
      if (k.endsWith('[]')) {
        const base = k.slice(0, -2);
        const all = sp.getAll(k);
        initFilters[base] = Array.from(new Set(all));
      } else if (k === 'hot') {
        // hot 파라미터는 배열로 저장
        initFilters.hot = [v];
      } else if (k === 'hot_year') {
        // hot_year 파라미터는 단일 값으로 저장
        initFilters.hot_year = v;
      } else if (k.endsWith('_conjunction')) {
        const base = k.replace('_conjunction', '');
        if (v === 'and') {
          initConjunctions[base] = 'and';
        }
      }
    });
    const initQuery = sp.get('sub_ks') || '';
    setSort(initSort);
    setFilters(initFilters);
    setPendingFilters(initFilters);
    setConjunctions(initConjunctions);
    setQuery(initQuery);
    setPendingQuery(initQuery);
    const extra = {};
    Object.entries(initFilters).forEach(([k, vals]) => {
      if (k === 'hot') {
        // hot 필터는 hot[] 대신 hot으로 첫 번째 값만 전달
        if (Array.isArray(vals) && vals.length) {
          extra.hot = vals[0];
        }
      } else if (k === 'hot_year') {
        // hot_year는 단일 값
        extra.hot_year = vals;
      } else if (Array.isArray(vals) && vals.length) {
        extra[`${k}[]`] = vals;
      }
    });
    Object.entries(initConjunctions).forEach(([k, v]) => {
      if (v === 'and') {
        extra[`${k}_conjunction`] = 'and';
      }
    });
    if (initQuery && initQuery.trim() !== '') extra.sub_ks = initQuery.trim();
    if (initSort.order_by) extra.order_by = initSort.order_by;
    if (initSort.order) extra.order = initSort.order;
    if (checkIfSearchOrFacetMode()) {
      setGraphExpanded(false);
    }
    fetchList(initPage, { extra });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navType]);

  // URL에 검색·패싯 관련 파라미터가 있으면 그래프 접기 (URL 변경 시마다 체크)
  useEffect(() => {
    if (checkIfSearchOrFacetMode()) {
      setGraphExpanded(false);
    }
  }, [location.search]);

  // keep scroll position in cache; avoid creating empty caches
  useEffect(() => {
    const onScroll = () => {
      const cached = loadCache();
      const now = Date.now();
      if (Array.isArray(rows) && rows.length > 0) {
        const next = {
          rows,
          pageInfo,
          facet,
          meta,
          filters,
          pendingFilters,
          conjunctions,
          sort,
          query,
          pendingQuery,
          scrollY: window.scrollY || 0,
          ts: now,
        };
        saveCache(next);
      } else if (isValidCache(cached)) {
        cached.scrollY = window.scrollY || 0;
        cached.ts = now;
        saveCache(cached);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [rows, pageInfo, facet, filters, pendingFilters, sort, query, pendingQuery]);

  const onPage = (p) => {
    if (!p || p === pageInfo.fromPage) return;
    syncUrl({ page: p });
    try {
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    } catch (e) {
      // no-op
    }
    fetchList(p);
  };

  if (err) return <div className="p-4 text-red-600">에러: {String(err)}</div>;

  // hot 필터에 따른 제목 매핑
  const hotTitleMap = {
    'wt2': 'World Top 2%',
    'hcr': 'Highly Cited Researchers',
    'hhi': 'Highest H-index',
    'op': 'Only Professors',
    'np': 'New Professors',
  };

  // 현재 선택된 hot 필터
  const currentHot = Array.isArray(filters.hot) && filters.hot.length > 0 ? filters.hot[0] : null;
  const hotTitle = currentHot ? hotTitleMap[currentHot] || 'Researchers' : 'Researchers';

  // 연도 데이터 가져오기
  const getYearData = () => {
    if (!currentHot) return [];
    const yearKey = `${currentHot}_years`;
    return Array.isArray(meta[yearKey]) ? meta[yearKey] : [];
  };

  const yearData = getYearData();
  const hasHotFilter = !!currentHot;
  const currentHotYear = filters.hot_year || null;

  return (
    <div className="researcherlistPage listPage flexPage mt-[127px] lg:mt-[120px] px-6 2xl:px-[100px]">
      <div className="lg:w-[310px] lg:mt-[45px]">
        <div className="highlight-resource-box fixed top-[100%] right-0 z-[101] w-full h-full transition-top duration-300 mb-8 p-8 bg-white rounded-xl lg:static">
          <h2 className="flex items-center gap-[6px] mb-5">
            <span className="text-[18px] font-bold">Hot Researchers</span>
          </h2>

          <button
            className="highlight-resource-box-close absolute top-8 right-8 w-[25px] h-[25px] cursor-pointer lg:hidden"
          >
            <img
              src={`${rmVars.themeUrl}images/clear-icon.png`}
              className="w-[18px]"
              alt="close"
            />
          </button>

          <div className="flex flex-col gap-2 lg:gap-1">
            <button
              onClick={(e) => {
                e.preventDefault();
                const isActive = Array.isArray(filters.hot) && filters.hot.includes('wt2');
                const newFilters = { ...filters };
                if (isActive) {
                  // 이미 선택되어 있으면 해제
                  const updatedHot = (filters.hot || []).filter(v => v !== 'wt2');
                  if (updatedHot.length > 0) {
                    newFilters.hot = updatedHot;
                  } else {
                    delete newFilters.hot;
                  }
                  delete newFilters.hot_year;
                } else {
                  // 선택되어 있지 않으면 선택
                  newFilters.hot = ['wt2'];
                  delete newFilters.hot_year;
                }
                setFilters(newFilters);
                const extra = {};
                Object.entries(newFilters).forEach(([k, vals]) => {
                  if (k === 'hot') {
                    // hot 필터는 hot[] 대신 hot으로 첫 번째 값만 전달
                    if (Array.isArray(vals) && vals.length) {
                      extra.hot = vals[0];
                    }
                  } else if (Array.isArray(vals) && vals.length) {
                    extra[`${k}[]`] = vals;
                  }
                });
                Object.entries(conjunctions).forEach(([k, conj]) => {
                  if (conj === 'and') {
                    extra[`${k}_conjunction`] = 'and';
                  }
                });
                if (sort?.order_by) extra.order_by = sort.order_by;
                if (sort?.order) extra.order = sort.order;
                const isWt2Selected = Array.isArray(newFilters.hot) && newFilters.hot[0] === 'wt2';
                if (isWt2Selected) {
                  extra.order_by = 'date';
                  extra.order = 'desc';
                }
                if (String(query || '').trim()) extra.sub_ks = String(query).trim();
                const sortForUrl = isWt2Selected ? { order_by: 'date', order: 'desc' } : sort;
                if (isWt2Selected) setSort(sortForUrl);
                syncUrl({ page: 1, filters: newFilters, sort: sortForUrl, conjunctions });
                try {
                  if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                  }
                } catch (err) { }
                fetchList(1, { extra });
              }}
              className={`px-[14px] py-[10px] rounded-md text-left cursor-pointer hover:bg-[#D1D5DB] transition-colors ${Array.isArray(filters.hot) && filters.hot.includes('wt2') ? 'active bg-[#D1D5DB]' : 'bg-[#F3F5F9]'}`}
            >
              <span className="text-[#376BDB] text-[14px] font-semibold">World Top 2%</span>
            </button>

            <button
              onClick={(e) => {
                e.preventDefault();
                const isActive = Array.isArray(filters.hot) && filters.hot.includes('hcr');
                const newFilters = { ...filters };
                if (isActive) {
                  // 이미 선택되어 있으면 해제
                  const updatedHot = (filters.hot || []).filter(v => v !== 'hcr');
                  if (updatedHot.length > 0) {
                    newFilters.hot = updatedHot;
                  } else {
                    delete newFilters.hot;
                  }
                  delete newFilters.hot_year;
                } else {
                  // 선택되어 있지 않으면 선택
                  newFilters.hot = ['hcr'];
                  delete newFilters.hot_year;
                }
                setFilters(newFilters);
                const extra = {};
                Object.entries(newFilters).forEach(([k, vals]) => {
                  if (k === 'hot') {
                    // hot 필터는 hot[] 대신 hot으로 첫 번째 값만 전달
                    if (Array.isArray(vals) && vals.length) {
                      extra.hot = vals[0];
                    }
                  } else if (Array.isArray(vals) && vals.length) {
                    extra[`${k}[]`] = vals;
                  }
                });
                Object.entries(conjunctions).forEach(([k, conj]) => {
                  if (conj === 'and') {
                    extra[`${k}_conjunction`] = 'and';
                  }
                });
                if (sort?.order_by) extra.order_by = sort.order_by;
                if (sort?.order) extra.order = sort.order;
                const isHcrSelected = Array.isArray(newFilters.hot) && newFilters.hot[0] === 'hcr';
                if (isHcrSelected) {
                  extra.order_by = 'date';
                  extra.order = 'desc';
                }
                if (String(query || '').trim()) extra.sub_ks = String(query).trim();
                const sortForUrl = isHcrSelected ? { order_by: 'date', order: 'desc' } : sort;
                if (isHcrSelected) setSort(sortForUrl);
                syncUrl({ page: 1, filters: newFilters, sort: sortForUrl, conjunctions });
                try {
                  if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                  }
                } catch (err) { }
                fetchList(1, { extra });
              }}
              className={`px-[14px] py-[10px] rounded-md text-left cursor-pointer hover:bg-[#D1D5DB] transition-colors ${Array.isArray(filters.hot) && filters.hot.includes('hcr') ? 'active bg-[#D1D5DB]' : 'bg-[#F3F5F9]'}`}
            >
              <span className="text-[#376BDB] text-[14px] font-semibold">Highly Cited Researchers</span>
            </button>

            <button
              onClick={(e) => {
                e.preventDefault();
                const isActive = Array.isArray(filters.hot) && filters.hot.includes('hhi');
                const newFilters = { ...filters };
                if (isActive) {
                  // 이미 선택되어 있으면 해제
                  const updatedHot = (filters.hot || []).filter(v => v !== 'hhi');
                  if (updatedHot.length > 0) {
                    newFilters.hot = updatedHot;
                  } else {
                    delete newFilters.hot;
                  }
                  delete newFilters.hot_year;
                } else {
                  // 선택되어 있지 않으면 선택
                  newFilters.hot = ['hhi'];
                  delete newFilters.hot_year;
                }
                setFilters(newFilters);
                const extra = {};
                Object.entries(newFilters).forEach(([k, vals]) => {
                  if (k === 'hot') {
                    // hot 필터는 hot[] 대신 hot으로 첫 번째 값만 전달
                    if (Array.isArray(vals) && vals.length) {
                      extra.hot = vals[0];
                    }
                  } else if (Array.isArray(vals) && vals.length) {
                    extra[`${k}[]`] = vals;
                  }
                });
                Object.entries(conjunctions).forEach(([k, conj]) => {
                  if (conj === 'and') {
                    extra[`${k}_conjunction`] = 'and';
                  }
                });
                if (sort?.order_by) extra.order_by = sort.order_by;
                if (sort?.order) extra.order = sort.order;
                const isHhiSelected = Array.isArray(newFilters.hot) && newFilters.hot[0] === 'hhi';
                if (isHhiSelected) {
                  extra.order_by = 'hindex';
                  extra.order = 'desc';
                }
                if (String(query || '').trim()) extra.sub_ks = String(query).trim();
                const sortForUrl = isHhiSelected ? { order_by: 'hindex', order: 'desc' } : sort;
                if (isHhiSelected) setSort(sortForUrl);
                syncUrl({ page: 1, filters: newFilters, sort: sortForUrl, conjunctions });
                try {
                  if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                  }
                } catch (err) { }
                fetchList(1, { extra });
              }}
              className={`px-[14px] py-[10px] rounded-md text-left cursor-pointer hover:bg-[#D1D5DB] transition-colors ${Array.isArray(filters.hot) && filters.hot.includes('hhi') ? 'active bg-[#D1D5DB]' : 'bg-[#F3F5F9]'}`}
            >
              <span className="text-[#376BDB] text-[14px] font-semibold">Highest H-index</span>
            </button>

            <button
              onClick={(e) => {
                e.preventDefault();
                const isActive = Array.isArray(filters.hot) && filters.hot.includes('op');
                const newFilters = { ...filters };
                if (isActive) {
                  // 이미 선택되어 있으면 해제
                  const updatedHot = (filters.hot || []).filter(v => v !== 'op');
                  if (updatedHot.length > 0) {
                    newFilters.hot = updatedHot;
                  } else {
                    delete newFilters.hot;
                  }
                  delete newFilters.hot_year;
                } else {
                  // 선택되어 있지 않으면 선택
                  newFilters.hot = ['op'];
                  delete newFilters.hot_year;
                }
                setFilters(newFilters);
                const extra = {};
                Object.entries(newFilters).forEach(([k, vals]) => {
                  if (k === 'hot') {
                    // hot 필터는 hot[] 대신 hot으로 첫 번째 값만 전달
                    if (Array.isArray(vals) && vals.length) {
                      extra.hot = vals[0];
                    }
                  } else if (Array.isArray(vals) && vals.length) {
                    extra[`${k}[]`] = vals;
                  }
                });
                Object.entries(conjunctions).forEach(([k, conj]) => {
                  if (conj === 'and') {
                    extra[`${k}_conjunction`] = 'and';
                  }
                });
                if (sort?.order_by) extra.order_by = sort.order_by;
                if (sort?.order) extra.order = sort.order;
                const isOpSelected = Array.isArray(newFilters.hot) && newFilters.hot[0] === 'op';
                if (isOpSelected) {
                  extra.order_by = 'date';
                  extra.order = 'desc';
                }
                if (String(query || '').trim()) extra.sub_ks = String(query).trim();
                const sortForUrl = isOpSelected ? { order_by: 'date', order: 'desc' } : sort;
                if (isOpSelected) setSort(sortForUrl);
                syncUrl({ page: 1, filters: newFilters, sort: sortForUrl, conjunctions });
                try {
                  if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                  }
                } catch (err) { }
                fetchList(1, { extra });
              }}
              className={`px-[14px] py-[10px] rounded-md text-left cursor-pointer hover:bg-[#D1D5DB] transition-colors ${Array.isArray(filters.hot) && filters.hot.includes('op') ? 'active bg-[#D1D5DB]' : 'bg-[#F3F5F9]'}`}
            >
              <span className="text-[#376BDB] text-[14px] font-semibold">Only Professors</span>
            </button>

            <button
              onClick={(e) => {
                e.preventDefault();
                const isActive = Array.isArray(filters.hot) && filters.hot.includes('np');
                const newFilters = { ...filters };
                if (isActive) {
                  // 이미 선택되어 있으면 해제
                  const updatedHot = (filters.hot || []).filter(v => v !== 'np');
                  if (updatedHot.length > 0) {
                    newFilters.hot = updatedHot;
                  } else {
                    delete newFilters.hot;
                  }
                  delete newFilters.hot_year;
                } else {
                  // 선택되어 있지 않으면 선택
                  newFilters.hot = ['np'];
                  delete newFilters.hot_year;
                }
                setFilters(newFilters);
                const extra = {};
                Object.entries(newFilters).forEach(([k, vals]) => {
                  if (k === 'hot') {
                    // hot 필터는 hot[] 대신 hot으로 첫 번째 값만 전달
                    if (Array.isArray(vals) && vals.length) {
                      extra.hot = vals[0];
                    }
                  } else if (Array.isArray(vals) && vals.length) {
                    extra[`${k}[]`] = vals;
                  }
                });
                Object.entries(conjunctions).forEach(([k, conj]) => {
                  if (conj === 'and') {
                    extra[`${k}_conjunction`] = 'and';
                  }
                });
                if (sort?.order_by) extra.order_by = sort.order_by;
                if (sort?.order) extra.order = sort.order;
                const isNpSelected = Array.isArray(newFilters.hot) && newFilters.hot[0] === 'np';
                if (isNpSelected) {
                  extra.order_by = 'date';
                  extra.order = 'desc';
                }
                if (String(query || '').trim()) extra.sub_ks = String(query).trim();
                const sortForUrl = isNpSelected ? { order_by: 'date', order: 'desc' } : sort;
                if (isNpSelected) setSort(sortForUrl);
                syncUrl({ page: 1, filters: newFilters, sort: sortForUrl, conjunctions });
                try {
                  if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                  }
                } catch (err) { }
                fetchList(1, { extra });
              }}
              className={`px-[14px] py-[10px] rounded-md text-left cursor-pointer hover:bg-[#D1D5DB] transition-colors ${Array.isArray(filters.hot) && filters.hot.includes('np') ? 'active bg-[#D1D5DB]' : 'bg-[#F3F5F9]'}`}
            >
              <span className="text-[#376BDB] text-[14px] font-semibold">New Professors</span>
            </button>
          </div>
        </div>

        <ResearchersFilterSidebar
          facet={facet}
          filters={filters}
          setFilters={setFilters}
          pendingFilters={pendingFilters}
          setPendingFilters={setPendingFilters}
          conjunctions={conjunctions}
          setConjunctions={setConjunctions}
          sort={sort}
          setSort={setSort}
          query={query}
          setQuery={setQuery}
          pendingQuery={pendingQuery}
          setPendingQuery={setPendingQuery}
          buildExtraParams={buildExtraParams}
          syncUrl={syncUrl}
          fetchList={fetchList}
          cleanFilters={cleanFilters}
          setPopup={setPopup}
          setGraphExpanded={setGraphExpanded}
        />
      </div>

      <div className="resource-list-cont w-full pt-[30px] lg:w-[calc(100%-360px)] lg:px-[0] lg:pt-[23px] lg:mt-[50px]">
        <PageBreadcrumb current="Researchers" />

        <div className="tit-area w-full flex justify-between gap-3">
          <h2 className="text-[26px] leading-[130%] font-black lg:text-[32px]">
            {hotTitle}
          </h2>
          <div className="flex items-center gap-2 shrink-0">
            <button className="btn-highlight-resource flex items-center justify-center w-[38px] h-[38px] bg-[#E4E8F1] rounded-[8px] cursor-pointer lg:hidden">
              <img src={`${rmVars.themeUrl}images/link-ico-6C7687.png`} className="w-[20px]" alt="filter" />
            </button>

            <button
              className="btn-filter flex items-center justify-center w-[38px] h-[38px] bg-[#E4E8F1] rounded-[8px] cursor-pointer lg:hidden">
              <img
                src={`${rmVars.themeUrl}images/filter-ico.png`}
                className="w-[20px]"
                alt="filter"
              />
            </button>
          </div>
        </div>

        {/* World Top 2% 설명 (wt2 선택 시에만 표시) */}
        {currentHot === 'wt2' && (
          <div className="bg-[#F5F6F8] px-0 pt-[10px] pb-[10px]">
            <p className="mb-0 text-[12px] leading-[1.5] text-[#9CA3AF] antialiased">
              &ldquo;World&rsquo;s Top 2% Scientists&rdquo; is released annually based on a Composite Score developed by the research team led by Professor John P. A. Ioannidis at Stanford University. Since 2019, it has been published to enable a fair and standardized comparison of research influence among scientists worldwide.
            </p>
            <p className="mt-2 mb-2 text-[12px] leading-[1.5] text-[#9CA3AF] antialiased">
              &ldquo;전 세계 상위 2% 연구자(World&rsquo;s Top 2% Scientists)&rdquo;는 스탠포드 대학의 존 이오아니디스(John P.A. Ioannidis) 교수 연구팀이 개발한 복합지표(Composite Score)를 바탕으로, 전 세계 연구자의 연구 영향력을 공정하고 표준화된 방식으로 비교·평가하기 위해 2019년부터 발표되고 있습니다.
            </p>
          </div>
        )}

        {/* HCR 설명 (hcr 선택 시에만 표시) */}
        {currentHot === 'hcr' && (
          <div className="bg-[#F5F6F8] px-0 pt-[10px] pb-[10px]">
            <p className="mb-0 text-[12px] leading-[1.5] text-[#9CA3AF] antialiased">
              Each year, the Highly Cited Researchers program from Clarivate highlights the world’s most influential scientists and social scientists — those whose published work ranks in the top 1% by citations for their field and year.
            </p>
            <p className="mt-2 mb-2 text-[12px] leading-[1.5] text-[#9CA3AF] antialiased">
              Clarivate의 Highly Cited Researchers(HCR) 프로그램은 매년 전 세계에서 가장 영향력 있는 자연과학자 및 사회과학자를 선정해 조명합니다. 선정 대상은 해당 연구자의 발표 논문 중에서 분야 및 출판연도 기준 인용 상위 1%에 해당하는 성과를 보유한 연구자입니다.
            </p>
          </div>
        )}

        {/* NP 설명 (np 선택 시에만 표시) */}
        {currentHot === 'np' && (
          <div className="bg-[#F5F6F8] px-0 pt-[10px] pb-[10px]">
            <p className="mb-0 text-[12px] leading-[1.5] text-[#9CA3AF] antialiased">
              Professors who have been newly appointed to the university within the past two years.
            </p>
            <p className="mt-2 mb-2 text-[12px] leading-[1.5] text-[#9CA3AF] antialiased">
              최근 2년 이내 우리 대학에 새롭게 임용된 전임교원을 보여줍니다.
            </p>
          </div>
        )}

        {/* 연도 탭 UI */}
        {hasHotFilter && yearData.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                const newFilters = { ...filters };
                delete newFilters.hot_year;
                setFilters(newFilters);
                const extra = buildExtraParams();
                delete extra.hot_year;
                syncUrl({ page: 1, filters: newFilters, sort, conjunctions });
                try {
                  if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                  }
                } catch (err) { }
                fetchList(1, { extra });
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${!currentHotYear
                  ? 'bg-[#6B7280] text-white'
                  : 'bg-[#F3F5F9] text-[#6B7280] hover:bg-[#D1D5DB]'
                }`}
            >
              All
            </button>
            {yearData.map((year) => (
              <button
                key={year}
                onClick={(e) => {
                  e.preventDefault();
                  const newFilters = { ...filters, hot_year: year };
                  setFilters(newFilters);
                  const extra = buildExtraParams();
                  extra.hot_year = year;
                  syncUrl({ page: 1, filters: newFilters, sort, conjunctions });
                  try {
                    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                    }
                  } catch (err) { }
                  fetchList(1, { extra });
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${currentHotYear === year
                    ? 'bg-[#6B7280] text-white'
                    : 'bg-[#F3F5F9] text-[#6B7280] hover:bg-[#D1D5DB]'
                  }`}
              >
                {year}
              </button>
            ))}
          </div>
        )}

        {!hasHotFilter && (
          <ResearcherGraphs graphExpanded={graphExpanded} setGraphExpanded={setGraphExpanded} />
        )}

        <div className={`listUtil ${hasHotFilter ? 'mt-2' : 'mt-10'} lg:flex items-center justify-between`}>
          <div className="mb-3 text-[14px] text-[#6C7687] lg:mb-0">
            Found <strong>{(pageInfo.totalResults && Number(pageInfo.totalResults).toLocaleString()) || 0}</strong> results
          </div>

          <ResearcherSortSelect sort={sort} onChange={(nextSort) => {
            setSort(nextSort);
            syncUrl({ page: 1, sort: nextSort });
            // 새로운 sort 값을 직접 사용하여 extra 파라미터 생성
            const extra = buildExtraParams();
            if (nextSort?.order_by) extra.order_by = nextSort.order_by;
            if (nextSort?.order) extra.order = nextSort.order;
            fetchList(1, { extra });
          }} />
        </div>

        <div className="relative mt-4 lg:mt-3 w-full min-w-0">
          {/* {loading && (
            <ChartSkeletonLoader show={loading} />
          )} */}
          {rows.length !== 0 && (
            <ul
              className="journal-list-wrap w-full min-w-0 flex flex-col gap-3 min-[800px]:flex-row min-[800px]:flex-wrap min-[800px]:gap-[28px]"
            >
              {rows.map((r) => (<ResearcherCard key={r.researcher_id || r.id || r.issn || Math.random()} item={r} highlightQuery={query} />))}
            </ul>
          )}

          {rows.length === 0 && !loading && (
            <div className="no-journal-list hidden">
              <div
                className="text-[13px] font-semibold text-center bg-white py-[72px] rounded-xl"
              >
                조건에 맞는 검색 결과가 없습니다.
              </div>
            </div>
          )}
        </div>

        <Paginator pageInfo={pageInfo} onPage={onPage} />

        <FacetPopup
          open={!!popup.open}
          title={popup.title}
          items={arr(facet?.[popup.key]?.result)}
          selected={pendingFilters?.[popup.key] || []}
          facetKey={popup.key}
          conjunction={popup.conjunction}
          conjunctionValue={popup.conjunctionValue}
          onConjunctionChange={popup.onConjunctionChange}
          onToggle={(value) => {
            const k = popup.key;
            if (!k) return;
            setPendingFilters(prev => {
              const prevArr = Array.isArray(prev[k]) ? prev[k] : [];
              const exists = prevArr.includes(value);
              const nextArr = exists ? prevArr.filter(v => v !== value) : [...prevArr, value];
              return { ...prev, [k]: nextArr };
            });
          }}
          onRefine={() => {
            const next = cleanFilters(pendingFilters || {});
            setFilters(next);
            setPendingFilters(next);
            // 그래프 닫기
            setGraphExpanded(false);
            const extra = {};
            Object.entries(next).forEach(([k, vals]) => {
              if (Array.isArray(vals) && vals.length) extra[`${k}[]`] = vals;
            });
            // conjunction 파라미터 추가
            Object.entries(conjunctions).forEach(([k, conj]) => {
              if (conj === 'and') {
                extra[`${k}_conjunction`] = 'and';
              }
            });
            if (sort?.order_by) extra.order_by = sort.order_by;
            if (sort?.order) extra.order = sort.order;
            syncUrl({ page: 1, filters: next, sort, query, conjunctions });
            try {
              if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
              }
            } catch (e) {
            }
            setPopup(p => ({ ...p, open: false }));
            fetchList(1, { extra });
          }}
          onCancel={() => setPopup(p => ({ ...p, open: false }))}
        />
      </div>
    </div>
  );
}
